// strokeLogWindow — 絵ログを「新しい側から」必要なぶんだけ読み足していく窓
// (荒らし復旧「人を外して戻す」③-b、exclusion-ui-investigation-2026-10-10.md)。
//
// `loadStrokeLogTimeline` の系譜モードは毎回「系譜の根まで」降りる。ここは逆に、
//   - 最初は新しい 4 本 (+ 現行文書なら live tail) だけ読む
//   - 呼び出し側が「どこまで要るか」(`LowerNeed`) を決め、足りなければ `step` で 1 ラウンド
//     (最大 4 本並列) ずつ古い側へ読み足す
// という形にして、荒らしが新しい側にいる通常の場合は数本で済ませる。
//
// 読むのは常に**新しい側から連続した本数** (listing を toSeq 降順に並べた先頭 k 本)。
// 範囲の下端は必ずセグメントの境目 = snapshot 世代の境目 (セグメントは snapshot を publish
// するたびに 1 本書かれる) になる。
//
// 「足りているか」の判定 (`isCovered`) は純粋に取得済みの本数とイベントの時刻だけで決め、
// 追加の余白 (G の時点で描きかけの線の pre-G イベントを拾う) として必ず**もう 1 本**古い
// セグメントまで読む。

import type { StrokeLogSegment } from '@oekaki/protocol';
import type { Logger } from '../../application/ports/Logger.js';
import { findDocumentStart, MAX_LINEAGE_DEPTH } from './lineageSeed.js';
import {
  type MergedStrokeLog,
  mergeSegments,
  type StrokeLogSegmentListEntry,
} from './segmentMerge.js';
import {
  defaultFetchFn,
  downloadSegment,
  type FetchFn,
  fetchStrokeLogListing,
  LINEAGE_DOWNLOAD_CONCURRENCY,
  type LoadStrokeLogTimelineOutcome,
  noopLogger,
  type SkippedSegment,
} from './strokeLogTimeline.js';

/** 窓の下端をどこまで下げたいか。 */
export type LowerNeed =
  /** `seq` 以降のイベントが全部入り、さらに 1 本古いセグメントまで (余白)。 */
  | { readonly kind: 'seq'; readonly seq: number }
  /** 最古側のセグメントの先頭イベントが `ts` 以前になる所まで、さらに 1 本古いセグメントまで。 */
  | { readonly kind: 'ts'; readonly ts: number }
  /** 無条件にもう 1 ラウンド読み足す (取れるものが尽きるまで繰り返す想定)。 */
  | { readonly kind: 'more' }
  /** 根まで (残り全部)。 */
  | { readonly kind: 'all' };

export interface StrokeLogWindowState extends MergedStrokeLog {
  readonly skippedSegments: readonly SkippedSegment[];
  /** まだ読んでいない古いセグメントが残っているか。false = ログ先頭まで読み終えた。 */
  readonly hasOlder: boolean;
  /** 取得済みの最も古いセグメントの fromSeq。1 本も無ければ null。 */
  readonly floorSeq: number | null;
  /** 取得済みの最も古いセグメントの toSeq。 */
  readonly floorToSeq: number | null;
  /** 取得済み listing セグメントの本数 (live tail は数えない)。 */
  readonly fetchedCount: number;
  /** listing セグメントの総数 (読む対象のうち)。 */
  readonly totalCount: number;
}

export interface StrokeLogWindow {
  state(): StrokeLogWindowState;
  isCovered(need: LowerNeed): boolean;
  /** 1 ラウンド読み足す。読む物が無い / 既に足りている / 中断されたときは false。 */
  step(need: LowerNeed): Promise<boolean>;
}

export interface OpenStrokeLogWindowInput {
  readonly dispatcherUrl: string;
  readonly roomId: string;
  readonly signal?: AbortSignal;
  readonly adminToken?: string;
  /**
   * 過去キャンバス行の文書を対象にするときの終端 seq (inclusive)。これより新しいセグメントは
   * 読まない。省略 = 現行文書 (最新まで)。
   */
  readonly endSeq?: number;
  /** 現行文書の未 flush 分 (live tail)。最初の 1 ラウンドの後に 1 回だけ呼ぶ。 */
  readonly liveTail?: (sinceSeq: number) => Promise<StrokeLogSegment | null>;
}

export interface StrokeLogWindowDeps {
  readonly fetchFn?: FetchFn;
  readonly logger?: Logger;
}

export type OpenStrokeLogWindowOutcome =
  | { readonly outcome: 'ok'; readonly window: StrokeLogWindow }
  | Exclude<LoadStrokeLogTimelineOutcome, { outcome: 'ok' }>;

interface FetchedInfo {
  /** この listing セグメントの先頭イベントの ts。イベントが無い / 読めなかった = null。 */
  readonly minTs: number | null;
}

export const openStrokeLogWindow = async (
  input: OpenStrokeLogWindowInput,
  deps: StrokeLogWindowDeps = {},
): Promise<OpenStrokeLogWindowOutcome> => {
  const fetchFn = deps.fetchFn ?? defaultFetchFn;
  const logger = deps.logger ?? noopLogger;
  const listing = await fetchStrokeLogListing(input, fetchFn, logger);
  if (listing.outcome !== 'ok') return listing;

  // 終端 seq 自身が境界 (全消し / restore) のとき、その行の文書は 1 つ手前で終わる。
  // 系譜モードと同じく `endSeq - 1` 以下から始まるセグメントだけを対象にする。
  const searchSeq = input.endSeq === undefined ? Number.POSITIVE_INFINITY : input.endSeq - 1;
  const candidates = listing.entries
    .filter((e) => e.fromSeq <= searchSeq)
    .sort((a, b) => (a.toSeq !== b.toSeq ? b.toSeq - a.toSeq : b.fromSeq - a.fromSeq));

  const segments: StrokeLogSegment[] = [];
  const skippedSegments: SkippedSegment[] = [];
  /** 取得済み (失敗含む) の listing セグメント。 */
  const fetched = new Map<string, FetchedInfo>();
  /**
   * 復元で捨てられた枝 `(restoredFromSeq, restoreSeq)`。系譜はこの間の描画を通らないので、
   * その中に収まるセグメントは読まない (復元元文書の根へ降りる今の規則と同じ。荒らしを
   * 巻き戻した枝を丸ごと読まずに済む)。マーカーを読んだ後にだけ分かる。
   */
  const abandoned: Array<{ readonly q: number; readonly m: number }> = [];
  let merged = mergeSegments([]);

  const aborted = (): boolean => input.signal?.aborted === true;

  /** 手元のイベントから系譜 (終端 → 復元元 → …) をたどり、捨てられた枝を求め直す。 */
  const recomputeAbandoned = (): void => {
    abandoned.length = 0;
    let cursor = searchSeq;
    for (let depth = 0; depth < MAX_LINEAGE_DEPTH; depth++) {
      const start = findDocumentStart(merged.events, cursor);
      if (start === null || start.kind !== 'restore') return;
      const { seq: m, restoredFromSeq: q } = start.marker;
      if (q >= m) return;
      abandoned.push({ q, m });
      cursor = q; // 復元元文書の続き
    }
  };

  /** 読む対象 (新しい順)。捨てられた枝に収まる未取得のセグメントは除く。 */
  const active = (): StrokeLogSegmentListEntry[] =>
    candidates.filter(
      (e) => fetched.has(e.url) || !abandoned.some((a) => e.fromSeq > a.q && e.toSeq < a.m),
    );
  const unfetched = (list: readonly StrokeLogSegmentListEntry[]) =>
    list.filter((e) => !fetched.has(e.url));

  const downloadNext = async (n: number): Promise<boolean> => {
    const batch = unfetched(active()).slice(0, n);
    if (batch.length === 0) return false;
    const results = await Promise.all(
      batch.map((e) => downloadSegment(e, fetchFn, input.signal, logger)),
    );
    for (let i = 0; i < batch.length; i++) {
      const r = results[i] as (typeof results)[number];
      const entry = batch[i] as StrokeLogSegmentListEntry;
      if ('segment' in r) {
        segments.push(r.segment);
        let minTs: number | null = null;
        for (const ev of r.segment.events) if (minTs === null || ev.ts < minTs) minTs = ev.ts;
        fetched.set(entry.url, { minTs });
      } else {
        skippedSegments.push(r.skipped);
        fetched.set(entry.url, { minTs: null });
      }
    }
    merged = mergeSegments(segments);
    recomputeAbandoned();
    return true;
  };

  /** need を満たすのに先頭から取得済みでなければならない本数 (`list` = 読む対象)。 */
  const neededPrefix = (list: readonly StrokeLogSegmentListEntry[], need: LowerNeed): number => {
    switch (need.kind) {
      case 'all':
      case 'more':
        return list.length;
      case 'seq': {
        // toSeq が seq 以上のもの + 余白 1 本 (list は toSeq 降順)
        let n = 0;
        for (const e of list) {
          if (e.toSeq < need.seq) break;
          n++;
        }
        return Math.min(list.length, n + 1);
      }
      case 'ts': {
        // 先頭イベントが ts 以前のセグメント + 余白 1 本。まだ見つからなければ全部 (= 読み続ける)。
        for (let i = 0; i < list.length; i++) {
          const info = fetched.get((list[i] as StrokeLogSegmentListEntry).url);
          if (info !== undefined && info.minTs !== null && info.minTs <= need.ts) {
            return Math.min(list.length, i + 2);
          }
        }
        return list.length;
      }
    }
  };

  const isCovered = (need: LowerNeed): boolean => {
    const list = active();
    if (unfetched(list).length === 0) return true;
    if (need.kind === 'more') return false;
    return unfetched(list.slice(0, neededPrefix(list, need))).length === 0;
  };

  // 最初の 1 ラウンド + live tail (現行文書のみ)。
  await downloadNext(LINEAGE_DOWNLOAD_CONCURRENCY);
  if (input.endSeq === undefined && input.liveTail !== undefined && !aborted()) {
    let since = 0;
    for (const r of merged.coverage) if (r.toSeq > since) since = r.toSeq;
    const tail = await input.liveTail(since);
    if (tail !== null) {
      segments.push(tail);
      merged = mergeSegments(segments);
      recomputeAbandoned();
    }
  }

  const window: StrokeLogWindow = {
    state: () => {
      let floor: StrokeLogSegmentListEntry | undefined;
      for (const e of candidates) {
        if (fetched.has(e.url) && (floor === undefined || e.fromSeq < floor.fromSeq)) floor = e;
      }
      const list = active();
      return {
        ...merged,
        skippedSegments,
        hasOlder: unfetched(list).length > 0,
        floorSeq: floor?.fromSeq ?? null,
        floorToSeq: floor?.toSeq ?? null,
        fetchedCount: fetched.size,
        totalCount: list.length,
      };
    },
    isCovered,
    step: async (need) => {
      if (aborted() || isCovered(need)) return false;
      const list = active();
      const want = unfetched(list.slice(0, neededPrefix(list, need))).length;
      return downloadNext(Math.min(LINEAGE_DOWNLOAD_CONCURRENCY, Math.max(1, want)));
    },
  };
  return { outcome: 'ok', window };
};
